import { DEFAULT_SITE } from './siteDefaults';
import { GALLERY_IMAGES } from './galleryImages';

function normalizeAlbumThumbMap(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    const key = typeof k === 'string' ? k.trim() : '';
    const url = typeof v === 'string' ? v.trim() : '';
    if (key && url) out[key] = url;
  }
  return out;
}

function orderedAlbumNames(g, items) {
  const configured = Array.isArray(g?.albums)
    ? [...new Set(g.albums.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean))]
    : [];
  const seen = new Set(configured);
  const extra = [];
  for (const item of items) {
    const name = typeof item?.album === 'string' && item.album.trim() ? item.album.trim() : '';
    if (!name || seen.has(name)) continue;
    seen.add(name);
    extra.push(name);
  }
  return [...configured, ...extra];
}

/**
 * Album cards for `/gallery`: title, thumbnail (CMS `albumThumbnails` or first photo in album).
 */
export function resolveGalleryAlbums(site) {
  const resolved = resolveGallery(site);
  const items = resolved.items || [];
  const g = site?.gallery;
  const thumbMap = normalizeAlbumThumbMap(g?.albumThumbnails);

  const hasAlbumField = items.some((item) => typeof item?.album === 'string' && item.album.trim());

  if (!hasAlbumField && items.length > 0) {
    const first = items[0];
    return [
      {
        title: resolved.sectionTitle || 'Gallery',
        thumbnail: first?.src || '',
        alt: first?.alt || '',
        itemCount: items.length,
      },
    ];
  }

  const names = orderedAlbumNames(g, items);
  const itemsByAlbum = new Map();
  for (const item of items) {
    const name = typeof item?.album === 'string' && item.album.trim() ? item.album.trim() : '';
    if (!name) continue;
    if (!itemsByAlbum.has(name)) itemsByAlbum.set(name, []);
    itemsByAlbum.get(name).push(item);
  }

  return names.map((title) => {
    const albumItems = itemsByAlbum.get(title) || [];
    const thumbnail = thumbMap[title] || albumItems[0]?.src || '';
    const alt = albumItems[0]?.alt || title;
    return {
      title,
      thumbnail,
      alt,
      itemCount: albumItems.length,
    };
  });
}

/**
 * Photos for one album. `albumId` is the album name (route param is URL-encoded).
 * Album names match CMS `gallery.items[].album`.
 */
export function resolveGalleryItemsForAlbum(site, albumIdParam) {
  const albumName = typeof albumIdParam === 'string' ? albumIdParam.trim() : '';
  const resolved = resolveGallery(site);
  const items = resolved.items || [];
  const hasAlbumField = items.some((item) => typeof item?.album === 'string' && item.album.trim());

  if (!hasAlbumField && items.length > 0) {
    const syntheticTitle = (resolved.sectionTitle || 'Gallery').trim();
    if (albumName === syntheticTitle) {
      return { albumTitle: syntheticTitle, items };
    }
    return { albumTitle: albumName, items: [] };
  }

  const filtered = items.filter(
    (item) => typeof item?.album === 'string' && item.album.trim() === albumName
  );
  return { albumTitle: albumName, items: filtered };
}

/** Cover image for an album: CMS `albumThumbnails[album]` when set, else first photo in the album. */
export function resolveAlbumCoverSrc(site, albumIdParam) {
  const albumName = typeof albumIdParam === 'string' ? albumIdParam.trim() : '';
  const thumbMap = normalizeAlbumThumbMap(site?.gallery?.albumThumbnails);
  if (albumName && thumbMap[albumName]) return thumbMap[albumName];
  const { items } = resolveGalleryItemsForAlbum(site, albumIdParam);
  return items[0]?.src || '';
}

/** Whether this album appears on the gallery index (known title). */
export function galleryAlbumExists(site, albumName) {
  const name = typeof albumName === 'string' ? albumName.trim() : '';
  if (!name) return false;
  return resolveGalleryAlbums(site).some((a) => a.title === name);
}

/** Public gallery copy + items from API, with static fallback when missing or empty `items`. */
export function resolveGallery(site) {
  const d = DEFAULT_SITE.gallery;
  const g = site?.gallery;
  if (g && typeof g === 'object' && Array.isArray(g.items)) {
    const items =
      g.items.length > 0
        ? g.items
        : GALLERY_IMAGES.map((im, i) => ({
            id: `g-static-${i}`,
            src: im.src,
            alt: im.alt,
            title: im.title || '',
          }));
    return {
      pageHeroImage: typeof g.pageHeroImage === 'string' ? g.pageHeroImage.trim() : '',
      sectionLabel: g.sectionLabel || d?.sectionLabel || 'Field gallery',
      sectionTitle: g.sectionTitle || d?.sectionTitle || 'Gallery',
      intro:
        g.intro ||
        d?.intro ||
        'A glimpse of fields, people, and the care that goes into every season.',
      items,
    };
  }
  return {
    pageHeroImage: typeof d?.pageHeroImage === 'string' ? d.pageHeroImage.trim() : '',
    sectionLabel: d?.sectionLabel || 'Field gallery',
    sectionTitle: d?.sectionTitle || 'Gallery',
    intro:
      d?.intro ||
      'A glimpse of fields, people, and the care that goes into every season.',
    items: GALLERY_IMAGES.map((im, i) => ({
      id: `g-static-${i}`,
      src: im.src,
      alt: im.alt,
      title: im.title || '',
    })),
  };
}
